import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Tooltip } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Typography } from "@mui/material";
import DOMPurify from "dompurify";

function createData(questions, result, score, data) {
  return {
    questions,
    result,
    score,
    data,
  };
}

function Row(props) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& td": {
            border: "none", // Change border color
          },
        }}
      >
        <TableCell
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            {row.question?.questionTitle}
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  marginTop: "8px",
                  border: "none",
                }}
              >
                <div
                  className="sun-editor"
                  style={{
                    borderColor: "transparent",
                    border: "none",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(row.question?.question || ""),
                  }}
                />
                {row.question?.answers?.map((elem, index) => {
                  const isNewStructure =
                    typeof elem === "object" && elem.optionId;

                  const rawCorrectAnswers =
                    row.question.correctAnswers ||
                    row.question.correctAnswer ||
                    [];
                  const correctAnswers = Array.isArray(rawCorrectAnswers)
                    ? rawCorrectAnswers
                    : [rawCorrectAnswers];
                  const isCorrect = isNewStructure
                    ? correctAnswers.includes(elem.optionId)
                    : correctAnswers.includes(elem);

                  const rawSelectedAnswers =
                    row.selectedAnswer || row.candidateAnswer || [];
                  const selectedAnswers = Array.isArray(rawSelectedAnswers)
                    ? rawSelectedAnswers
                    : [rawSelectedAnswers];
                  const isSelected = isNewStructure
                    ? selectedAnswers.includes(elem.optionId)
                    : selectedAnswers.includes(elem);

                  const textColor = isCorrect
                    ? "#00C49A"
                    : isSelected
                      ? "#F94948"
                      : "unset";
                  return (
                    <Typography
                      key={index}
                      sx={{
                        color: textColor,
                        fontWeight: isCorrect ? "600" : "400", // Bold for correct answers
                        fontSize: "13px",
                        fontFamily: "Roboto",
                      }}
                    >
                      {String.fromCharCode(65 + index)}.{" "}
                      {isNewStructure ? (
                        <span
                          className="sun-editor-editable"
                          style={{
                            display: "inline-block",
                            padding: 0,
                            color: textColor,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(elem.option),
                          }}
                        />
                      ) : (
                        elem
                      )}
                    </Typography>
                  );
                })}
              </Box>
            </Collapse>
          </TableCell>
        </TableCell>
        <TableCell align="center">
          {row.isAttempted ? (
            row.obtainedScore > 0 ? (
              <Tooltip title="Correct" arrow>
                <img
                  src="/assets/candidate_details_question_report_1.svg"
                  alt="Correct"
                />
              </Tooltip>
            ) : (
              <Tooltip title="Wrong" arrow>
                <img
                  src="/assets/candidate_details_question_report_2.svg"
                  alt="Wrong"
                />
              </Tooltip>
            )
          ) : (
            <Tooltip title="Not Attempted" arrow>
              <img
                width="17"
                height="18"
                src="/assets/not-allowed.svg"
                alt="Not Attempted"
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="center">
          {row.obtainedScore}/{row.question.score}
        </TableCell>
      </TableRow>
      <TableRow></TableRow>
    </React.Fragment>
  );
}

export default function CollapsibleTable({ data, selectedOption }) {
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    if (data && data.skills) {
      setSkills(data.skills);
    }
  }, [data]);

  const hasNoData = skills.every((skill) => {
    return (
      !skill[selectedOption]?.easyQuestions?.length &&
      !skill[selectedOption]?.mediumQuestions?.length &&
      !skill[selectedOption]?.hardQuestions?.length
    );
  });

  return (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F2F4F8", height: "36px" }}>
            <TableCell align="center">#</TableCell>
            <TableCell>
              {selectedOption === "mcqQuestions"
                ? "MCQ Questions"
                : selectedOption === "programmingQuestions"
                  ? "Programming Questions"
                  : "SQL Questions"}
            </TableCell>
            <TableCell align="center">Result</TableCell>
            <TableCell align="center">Score</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {hasNoData ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    marginTop: "20px",
                  }}
                >
                  <img
                    src="/assets/NoData.svg"
                    alt="No data found"
                    style={{
                      height: "60px",
                      width: "auto",
                    }}
                  />
                  No data found
                </Typography>{" "}
              </TableCell>
            </TableRow>
          ) : (
            skills.map((skill, index) => (
              <React.Fragment key={index}>
                {selectedOption === "mcqQuestions" && (
                  <>
                    {skill?.mcqQuestions?.easyQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`mcq-easy-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.mcqQuestions?.mediumQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`mcq-medium-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.mcqQuestions?.hardQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`mcq-hard-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                  </>
                )}
                {selectedOption === "programmingQuestions" && (
                  <>
                    {skill?.programmingQuestions?.easyQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`prog-easy-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.programmingQuestions?.mediumQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`prog-medium-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.programmingQuestions?.hardQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`prog-hard-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                  </>
                )}
                {selectedOption === "sqlQuestions" && (
                  <>
                    {skill?.sqlQuestions?.easyQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`sql-easy-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.sqlQuestions?.mediumQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`sql-medium-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                    {skill?.sqlQuestions?.hardQuestions?.map(
                      (question, qIndex) => (
                        <Row
                          key={`sql-hard-${index}-${qIndex}`}
                          row={question}
                        />
                      ),
                    )}
                  </>
                )}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
